import { type V2, angleTo, clamp, dirFromAngle, dist, normalize, sub } from '../core/math';
import type { Actor, CastState, TimelineEntry } from './actor';
import { isDisabled } from './combat';
import { forEachPassive } from './passives';
import { registry } from './registry';
import { effectiveCooldown } from './skills';
import type { ResolvedSkill, SkillAction, SkillStage } from './skillTypes';
import { getStatus } from './status';
import type { ActionKey } from './types';
import type { World } from './world';

export const INPUT_BUFFER = 0.25;
export const DEFAULT_COMBO_WINDOW = 0.7;
export const DEFAULT_CHAIN_WINDOW = 2.5;

export type CastBlock =
  | 'dead'
  | 'disabled'
  | 'silenced'
  | 'cooldown'
  | 'mana'
  | 'resource'
  | 'identity'
  | 'uses'
  | 'down'
  | 'locked';

/** Expands multi-hit actions into individual timeline entries. */
export function buildTimeline(actions: readonly SkillAction[]): TimelineEntry[] {
  const out: TimelineEntry[] = [];
  for (const a of actions) {
    if (a.kind === 'hit' && (a.hit.count ?? 1) > 1 && (a.hit.interval ?? 0) > 0) {
      const n = a.hit.count ?? 1;
      for (let i = 0; i < n; i++) out.push({ at: a.at + i * (a.hit.interval ?? 0), action: a, split: 1 / n });
    } else {
      out.push({ at: a.at, action: a, split: 1 });
    }
  }
  out.sort((x, y) => x.at - y.at);
  return out;
}

function lastActionTime(st: SkillStage): number {
  let t = 0;
  for (const e of buildTimeline(st.actions)) t = Math.max(t, e.at);
  return t;
}

export function isSilenced(a: Actor): boolean {
  return a.statuses.some((s) => getStatus(s.id).cc?.silence);
}

export function cooldownRemaining(a: Actor, skillId: string): number {
  return a.cooldowns.get(skillId) ?? 0;
}

export function chargesAvailable(a: Actor, s: ResolvedSkill): number {
  if (!s.charges || s.charges <= 1) return cooldownRemaining(a, s.id) > 0 ? 0 : 1;
  return a.charges.get(s.id) ?? s.charges;
}

export function canCast(world: World, a: Actor, s: ResolvedSkill): CastBlock | null {
  if (!a.alive) return 'dead';
  if (a.downTimer > 0 || a.airTimer > 0) return 'down';
  if (isDisabled(a)) return 'disabled';
  if (isSilenced(a) && s.kind !== 'dash') return 'silenced';
  if (chargesAvailable(a, s) <= 0) return 'cooldown';
  if (s.mana > 0 && a.mp < s.mana && !a.player?.pvpMode) return 'mana';
  if (s.kind === 'awakening' && a.player && a.player.awakeningUses <= 0) return 'uses';
  const cls = a.player ? registry.classes.get(a.player.classId) : undefined;
  if (cls?.identity.canCast && !cls.identity.canCast(a, s)) return 'identity';
  if (s.resourceCost && a.player) {
    const have = a.player.identity.data[s.resourceCost.kind] ?? a.player.identity.stacks;
    if (have < s.resourceCost.amount) return 'resource';
  }
  void world;
  return null;
}

/** True when the current cast may be replaced by a new action now. */
export function castCancelable(c: CastState, byDash: boolean): boolean {
  if (byDash) return true;
  if (c.phase === 'holding' || c.phase === 'charging' || c.phase === 'casting') return false;
  return c.t >= c.cancelAt;
}

function stageFor(s: ResolvedSkill, stage: number): SkillStage | undefined {
  return s.stages[Math.min(stage, s.stages.length - 1)];
}

function startCooldown(world: World, a: Actor, s: ResolvedSkill, extra = 0): void {
  const cd = effectiveCooldown(s, a.stats.cdr) + extra;
  if (s.charges && s.charges > 1) {
    const have = a.charges.get(s.id) ?? s.charges;
    a.charges.set(s.id, have - 1);
    if ((a.cooldowns.get(s.id) ?? 0) <= 0) a.cooldowns.set(s.id, cd);
  } else {
    a.cooldowns.set(s.id, cd);
  }
  void world;
}

export interface StartCastOptions {
  key?: ActionKey | null;
  stage?: number;
  /** Replaying a cast that another peer started: skip costs and checks. */
  remote?: boolean;
  castId?: number;
  aimAngle?: number;
}

/**
 * Begins (a stage of) a skill. For local actors this validates and pays the
 * costs; remote replays skip validation because the owner already did it.
 */
export function startCast(world: World, a: Actor, s: ResolvedSkill, aimPoint: V2, opts: StartCastOptions = {}): CastState | null {
  const stage = opts.stage ?? 0;
  const followUp = stage > 0;
  if (!opts.remote && !followUp) {
    const block = canCast(world, a, s);
    if (block) {
      world.lastCastBlock = block;
      return null;
    }
  }

  // Replace the current cast.
  if (a.cast) world.endCast(a, true);

  let point = { ...aimPoint };
  if (s.maxRange && !opts.remote) {
    const d = dist(a.pos, point);
    if (d > s.maxRange) {
      const dir = normalize(sub(point, a.pos));
      point = { x: a.pos.x + dir.x * s.maxRange, z: a.pos.z + dir.z * s.maxRange };
    }
  }
  const aimAngle = opts.aimAngle ?? (dist(a.pos, point) > 0.05 ? angleTo(a.pos, point) : a.facing);
  a.facing = aimAngle;
  a.path = [];
  a.moveGoal = null;

  if (!opts.remote && !followUp) {
    if (s.mana > 0 && !a.player?.pvpMode) a.mp -= s.mana;
    if (s.resourceCost && a.player) {
      const k = s.resourceCost.kind;
      if (k in a.player.identity.data) a.player.identity.data[k] = (a.player.identity.data[k] ?? 0) - s.resourceCost.amount;
      else a.player.identity.stacks -= s.resourceCost.amount;
    }
    if (s.kind === 'awakening' && a.player) a.player.awakeningUses--;
    const deferred = s.castType === 'combo' || s.castType === 'chain';
    if (!deferred) startCooldown(world, a, s);
    const cls = a.player ? registry.classes.get(a.player.classId) : undefined;
    cls?.identity.onSkillCast?.(world, a, s);
    forEachPassive(a, (h) => h.onCast?.(world, a, s));
  }

  const c: CastState = {
    castId: opts.castId ?? world.nextCastId(),
    skill: s,
    key: opts.key ?? null,
    stage,
    phase: 'stage',
    t: 0,
    aimAngle,
    aimPoint: point,
    next: 0,
    timeline: [],
    holdTime: 0,
    held: true,
    tickTimer: 0,
    chargeLevel: 0,
    perfect: false,
    queued: false,
    duration: 0,
    cancelAt: 0,
    mult: 1,
    areaScale: 1,
    pre: false,
  };
  a.cast = c;

  if (!followUp && s.preStage) {
    c.pre = true;
    enterStage(world, a, c, s.preStage);
  } else {
    enterMainPhase(world, a, c, followUp);
  }
  return c;
}

/** Enters the skill's main phase: casting bar, holding, charging or stage. */
function enterMainPhase(world: World, a: Actor, c: CastState, followUp: boolean): void {
  const s = c.skill;
  c.pre = false;
  if (!followUp && s.castTime && s.castTime > 0) {
    enterPhase(world, a, c, 'casting');
  } else if (!followUp && s.holding) {
    enterPhase(world, a, c, 'holding');
  } else if (!followUp && s.charge) {
    enterPhase(world, a, c, 'charging');
  } else {
    enterStage(world, a, c, stageFor(s, c.stage));
  }
}

function speedOf(a: Actor, s: ResolvedSkill): number {
  return Math.max(0.3, a.stats.atkSpeed * s.atkSpeedMult);
}

/** Cast-bar speed from statuses (`castSpeedPct`); stacks with attack speed. */
export function castSpeedOf(a: Actor): number {
  return Math.max(0.2, 1 + a.stats.mods.castSpeedPct);
}

function enterPhase(world: World, a: Actor, c: CastState, phase: 'casting' | 'holding' | 'charging'): void {
  const s = c.skill;
  c.phase = phase;
  c.t = 0;
  c.next = 0;
  c.timeline = [];
  if (phase === 'casting') {
    c.duration = s.castTime ?? 0;
    c.cancelAt = Infinity;
  } else if (phase === 'holding' && s.holding) {
    c.duration = s.holding.maxHold;
    c.cancelAt = Infinity;
    c.tickTimer = 0;
  } else if (phase === 'charging' && s.charge) {
    const last = s.charge.levels[s.charge.levels.length - 1] ?? 1;
    c.duration = Math.max(last, s.charge.overcharge?.[1] ?? 0);
    c.cancelAt = Infinity;
  }
  const anim = phase === 'holding' ? (s.holding?.anim ?? 'Cast') : phase === 'charging' ? (s.charge?.anim ?? 'Cast') : 'Cast';
  world.emit({
    type: 'castStage',
    actorId: a.id,
    castId: c.castId,
    skillId: s.id,
    stage: c.stage,
    phase,
    anim,
    aimAngle: c.aimAngle,
    aimPoint: c.aimPoint,
    duration: c.duration,
  });
}

function enterStage(world: World, a: Actor, c: CastState, st: SkillStage | undefined): void {
  if (!st) {
    world.endCast(a, false);
    return;
  }
  const speed = speedOf(a, c.skill);
  c.phase = 'stage';
  c.t = 0;
  c.next = 0;
  c.timeline = buildTimeline(st.actions);
  c.duration = st.duration;
  c.cancelAt = st.cancelAt ?? Math.min(st.duration, lastActionTime(st) + 0.05);
  world.emit({
    type: 'castStage',
    actorId: a.id,
    castId: c.castId,
    skillId: c.skill.id,
    stage: c.stage,
    phase: 'stage',
    anim: st.anim,
    aimAngle: c.aimAngle,
    aimPoint: c.aimPoint,
    duration: st.duration / speed,
  });
}

/** Called when the key of a holding/charge skill is released. */
export function releaseCast(a: Actor, key: ActionKey): void {
  if (a.cast && a.cast.key === key) a.cast.held = false;
}

/** Advances an actor's cast by dt. */
export function updateCast(world: World, a: Actor, dt: number): void {
  const c = a.cast;
  if (!c) return;
  const s = c.skill;
  const speed = speedOf(a, s);
  const local = world.isLocal(a);

  // Channelled skills can turn toward the cursor.
  if (local && c.phase !== 'casting') {
    const st = c.phase === 'stage' ? stageFor(s, c.stage) : undefined;
    const turnRate = c.phase === 'holding' ? 3 : (st?.turnRate ?? 0);
    const aim = world.aimOf(a);
    if (turnRate > 0 && aim) {
      const target = angleTo(a.pos, aim);
      const d = Math.atan2(Math.sin(target - c.aimAngle), Math.cos(target - c.aimAngle));
      const step = clamp(d, -turnRate * dt, turnRate * dt);
      c.aimAngle += step;
      a.facing = c.aimAngle;
    }
  }

  switch (c.phase) {
    case 'casting': {
      c.t += dt * speed * castSpeedOf(a);
      if (c.t >= c.duration) enterStage(world, a, c, stageFor(s, c.stage));
      return;
    }
    case 'holding': {
      const h = s.holding;
      if (!h) return;
      c.t += dt;
      c.holdTime += dt;
      c.tickTimer -= dt * speed;
      while (c.tickTimer <= 0 && c.holdTime < h.maxHold) {
        c.tickTimer += h.tickInterval;
        for (const act of h.tick) world.fireAction(a, c, act, 1);
      }
      const done = (local && !c.held) || c.holdTime >= h.maxHold || (!local && !c.held);
      if (done) {
        const pz = h.perfectZone;
        c.perfect = !!pz && c.holdTime >= pz[0] && c.holdTime <= pz[1];
        c.mult = c.perfect ? (h.perfectMult ?? 1) : 1;
        if (h.finisher) {
          world.emit({ type: 'identity', actorId: a.id, op: c.perfect ? 'perfect' : 'release' });
          c.stage = 0;
          enterStage(world, a, c, h.finisher);
        } else {
          world.endCast(a, false);
        }
      }
      return;
    }
    case 'charging': {
      const ch = s.charge;
      if (!ch) return;
      c.t += dt;
      c.holdTime += dt * speed;
      let lvl = 0;
      ch.levels.forEach((th, i) => {
        if (c.holdTime >= th) lvl = i + 1;
      });
      c.chargeLevel = lvl;
      const maxT = c.duration;
      if ((!c.held && local) || (!local && !c.held) || c.holdTime >= maxT + 0.25) {
        const over = ch.overcharge && c.holdTime >= ch.overcharge[0] && c.holdTime <= ch.overcharge[1];
        c.perfect = !!over;
        c.mult = over ? (ch.overMult ?? 1) : (ch.mults[lvl] ?? 1);
        c.areaScale = over ? (ch.overAreaScale ?? 1) : (ch.areaScales?.[lvl] ?? 1);
        enterStage(world, a, c, stageFor(s, 0));
      }
      return;
    }
    case 'stage': {
      c.t += dt * speed;
      while (c.next < c.timeline.length) {
        const e = c.timeline[c.next] as TimelineEntry;
        if (e.at > c.t) break;
        c.next++;
        world.fireAction(a, c, e.action, e.split);
        if (a.cast !== c) return; // action ended/replaced the cast
      }
      if (c.pre) {
        if (c.t >= c.duration) enterMainPhase(world, a, c, false);
        return;
      }
      // Combo continues immediately once the stage can be cancelled.
      const more = c.stage + 1 < s.stages.length && (s.castType === 'combo' || s.castType === 'chain');
      if (more && c.queued && c.t >= c.cancelAt) {
        c.queued = false;
        c.stage++;
        if (local) {
          const aim = world.aimOf(a);
          if (aim && dist(a.pos, aim) > 0.05) c.aimAngle = angleTo(a.pos, aim);
          a.facing = c.aimAngle;
        }
        world.noteStageUsed(a, s);
        enterStage(world, a, c, stageFor(s, c.stage));
        return;
      }
      if (more && !a.player && c.t >= c.duration) {
        // Monsters chain multi-stage attacks automatically.
        c.stage++;
        enterStage(world, a, c, stageFor(s, c.stage));
        return;
      }
      if (c.t >= c.duration) {
        if (more && a.player && local) {
          // Open a follow-up window; the cast itself ends so the player can move.
          const window = s.followWindow ?? (s.castType === 'chain' ? DEFAULT_CHAIN_WINDOW : DEFAULT_COMBO_WINDOW);
          const used = (a.player.followUp?.skillId === s.id ? a.player.followUp.stagesUsed : 1) || 1;
          a.player.followUp = { skillId: s.id, stage: c.stage + 1, until: world.time + window, key: c.key ?? 'Q', stagesUsed: used };
          world.endCast(a, false, true);
        } else {
          world.endCast(a, false);
        }
      }
      return;
    }
  }
}

/** Finishes a combo/chain sequence: starts its cooldown (plus chain penalties). */
export function finishSequence(world: World, a: Actor, s: ResolvedSkill, stagesUsed: number): void {
  if (s.castType !== 'combo' && s.castType !== 'chain') return;
  const penalty = s.castType === 'chain' ? Math.max(0, stagesUsed - 1) * (s.chainPenalty ?? 0) : 0;
  startCooldown(world, a, s, penalty);
}

/** Direction the actor is aiming at for a given target point. */
export function aimDir(a: Actor, point: V2): V2 {
  const d = sub(point, a.pos);
  return Math.hypot(d.x, d.z) > 0.05 ? normalize(d) : dirFromAngle(a.facing);
}
